// Vinterläget under Inställningar (b10 avsnitt 4 A), bara för ägaren och för en båt med ett nav
// ombord. Tre spärrar, och knapparna visas bara när alla tre är på:
//   - fas 1: "Laddaren av" och "Laddaren på" per rad i vitlistan, med en fråga först som säger
//     vad som skrivs, till vilken enhet och vad det betyder. Efter skrivningen visar kortet
//     värdet som lästes tillbaka, laddarens läge och strömmen i banken, så att Jesper ser om MLi
//     skriver över värdet;
//   - fas 2: vinterläget automatiskt, med gränserna, på och av med en fråga först.
//
// b11 (BK33): gränserna ändras här: laddaren av och på, och varningen under och över. Molnet
// kontrollerar dem, och ändringen står i platsens logg. Och en Varning per rad när navet inte
// skriver till enheten, eftersom BusID säger emot.

import {
  datumOchTid,
  type Platsinstallningar,
  sedan,
  tal,
  type Vinterrad,
} from "@batkoll/gemensam";
import type { ComponentChildren } from "preact";
import { useState } from "preact/hooks";
import { api, Felsvar } from "../api.ts";
import { Kort, Rad } from "../komponenter.tsx";

const lasTal = (s: string) => (s.trim() ? Number(s.trim().replace(",", ".")) : Number.NaN);
const visaTal = (v: number) => String(v).replace(".", ",");

const ord = (v: boolean | null) =>
  v === null ? "okänd, navet har inte sagt något" : v ? "på" : "av";

const STATUS: Record<string, string> = {
  planerad: "väntar på navet",
  skickad: "skickad till navet",
  klar: "klart",
  fel: "gick inte",
  sparrad: "spärrat på platsen",
  avstangd: "styrningen var av",
};

function Laddarrad(props: {
  r: Vinterrad;
  nu: number;
  kanSkriva: boolean;
  fraga: (pa: boolean) => void;
}) {
  const r = props.r;
  const bank = [
    r.soc ? `${tal(r.soc.procent, 0)} %` : null,
    r.strom ? `${tal(r.strom.a, 1)} A` : null,
  ].filter(Boolean);
  const s = r.senaste;
  return (
    <div class="vinterrad">
      <h3>
        {r.enhet}: {r.variabel} (tab {r.tab}, index {r.index})
      </h3>
      <Rad namn="Variabeln">
        {r.varde
          ? `${r.varde.pa ? "1, laddaren på" : "0, laddaren av"} (${sedan(r.varde.tid, props.nu)})`
          : "inte läst än"}
      </Rad>
      <Rad namn="Laddaren">{r.laddare ? r.laddare.text : "inget värde än"}</Rad>
      {r.identitet && (
        <p class="meddelande">
          Varning: navet skriver inte till {r.enhet}. {r.identitet}.
        </p>
      )}
      <Rad namn={`Banken (${r.batteri})`}>
        {bank.length > 0 ? bank.join(", ") : "inget värde än"}
      </Rad>
      {s && (
        <p class="liten">
          Senast: laddaren {s.pa ? "på" : "av"}, {s.vem || "appen"} {datumOchTid(s.tid)}:{" "}
          {STATUS[s.status] ?? s.status}.{s.resultat ? ` ${s.resultat}` : ""}
          {s.tillbakalast ? ` Läst tillbaka: ${s.tillbakalast}.` : ""}
        </p>
      )}
      {props.kanSkriva && (
        <div class="knapprad">
          <button type="button" class="knapp-andra" onClick={() => props.fraga(false)}>
            Laddaren av
          </button>
          <button type="button" class="knapp-andra" onClick={() => props.fraga(true)}>
            Laddaren på
          </button>
        </div>
      )}
    </div>
  );
}

export function Vinterdel(props: {
  s: Platsinstallningar;
  nu: number;
  spara: (sektion: string, gor: () => Promise<Platsinstallningar>, ok?: string) => Promise<boolean>;
  beskedrad: ComponentChildren;
}) {
  const v = props.s.vinterlage;
  const [fraga, setFraga] = useState<{ rad: string; pa: boolean } | null>(null);
  const [fas2, setFas2] = useState(false);
  const g = v?.granser;
  const [av, setAv] = useState(g ? visaTal(g.av.varde) : "");
  const [pa, setPa] = useState(g ? visaTal(g.pa.varde) : "");
  const [under, setUnder] = useState(g ? visaTal(g.varningUnder.varde) : "");
  const [over, setOver] = useState(g ? visaTal(g.varningOver.varde) : "");
  if (!v || props.s.plats.roll !== "agare") return null;
  const id = props.s.plats.id;
  const sp = v.sparrar;
  const vald = fraga && v.rader.find((r) => r.rad === fraga.rad);

  return (
    <Kort rubrik="Vinterläge">
      <p class="liten">
        Laddarna slås av när batterierna har laddats nog, och på igen när de har sjunkit, så att
        litiumbatterierna står på ungefär {v.paProcent}–{v.avProcent} % under vintern. Navet skriver
        bara två variabler på MasterBus, och bara när alla tre spärrarna är på.
      </p>
      <Rad namn="Styrningen i molnet">{sp.molnet ? "på" : "av"}</Rad>
      <Rad namn="Styrningen på platsen">{ord(sp.platsen)}</Rad>
      <Rad namn="Skrivningen på Pi:n">
        {ord(sp.pi)}
        {sp.pi === false && sp.piOrsak ? ` (${sp.piOrsak})` : ""}
      </Rad>
      {!v.kanSkriva && <p class="meddelande">Knapparna visas när alla tre spärrarna är på.</p>}
      {v.rader.map((r) => (
        <Laddarrad
          key={r.rad}
          r={r}
          nu={props.nu}
          kanSkriva={v.kanSkriva}
          fraga={(pa) => setFraga({ rad: r.rad, pa })}
        />
      ))}
      {fraga && vald && (
        <div class="bekraftelse" role="dialog" aria-label="Bekräfta skrivningen">
          <p class="meddelande">
            Skriv {fraga.pa ? 1 : 0} till {vald.variabel} (tab {vald.tab}, index {vald.index}) i{" "}
            {vald.enhet}?
          </p>
          <p class="liten">
            Det slår {fraga.pa ? "på" : "av"} laddaren i {vald.enhet}, som {vald.gor}.{" "}
            {fraga.pa
              ? "Det är det vanliga läget."
              : "Combin är fortfarande på: landströmmen går igenom till 230 V ombord och till CM12/25, som laddar motorbatterierna. Titta efter om laddaren slås på igen av sig själv, till exempel av MLi."}{" "}
            Navet läser tillbaka värdet efter skrivningen, och svaret kommer här inom ett par
            minuter.
          </p>
          <div class="knapprad">
            <button
              type="button"
              class="knapp-huvud"
              onClick={async () => {
                const f = fraga;
                setFraga(null);
                await props.spara(
                  "vinter",
                  () => api.laddare(id, f.rad, f.pa),
                  "Skickat till navet. Svaret och värdet som lästes tillbaka kommer inom ett par minuter.",
                );
              }}
            >
              Ja, skriv {fraga.pa ? 1 : 0}
            </button>
            <button type="button" class="knapp-andra" onClick={() => setFraga(null)}>
              Avbryt
            </button>
          </div>
        </div>
      )}
      <h3>Automatiskt</h3>
      <Rad namn="Vinterläget">{v.pa ? `på (${v.paProcent}–${v.avProcent} %)` : "av"}</Rad>
      <Rad namn="I navet">{v.lage ?? "navet har inte sagt något"}</Rad>
      <p class="liten">
        Laddaren av när banken når {v.avProcent} %, och på igen vid {v.paProcent} %. MLi6000 styr
        Masscombi, och MLi1250 styr Plus 1250. Slår MLi på laddaren igen, slåss navet inte: det
        väntar till nästa gräns, och slår av vinterläget efter fler än tre gånger på ett dygn. En
        Varning om en bank hamnar under {v.varningUnder} % eller över {v.varningOver} %, och om
        navet är tyst i två timmar.
      </p>
      {!fas2 ? (
        (v.pa || v.kanSkriva) && (
          <button type="button" class="knapp-andra" onClick={() => setFas2(true)}>
            {v.pa ? "Slå av vinterläget" : "Slå på vinterläget"}
          </button>
        )
      ) : (
        <div class="bekraftelse" role="dialog" aria-label="Bekräfta vinterläget">
          <p class="meddelande">
            {v.pa ? "Slå av vinterläget? Navet slår på laddarna igen." : "Slå på vinterläget?"}
          </p>
          {!v.pa && (
            <p class="liten">
              Navet slår då själv av laddaren i Masscombi och i Plus 1250 vid {v.avProcent} %, och
              på igen vid {v.paProcent} %.
            </p>
          )}
          <div class="knapprad">
            <button
              type="button"
              class="knapp-huvud"
              onClick={async () => {
                setFas2(false);
                await props.spara(
                  "vinter",
                  () => api.vinterlage(id, !v.pa),
                  v.pa ? "Vinterläget är av." : "Vinterläget är på.",
                );
              }}
            >
              {v.pa ? "Ja, slå av" : "Ja, slå på"}
            </button>
            <button type="button" class="knapp-andra" onClick={() => setFas2(false)}>
              Avbryt
            </button>
          </div>
        </div>
      )}
      <div class="knapprad">
        <button
          type="button"
          class="knapp-andra"
          onClick={() => props.spara("vinter", () => api.installningar(id), "Hämtat.")}
        >
          Hämta läget igen
        </button>
      </div>
      {g && (
        <form
          class="formular"
          onSubmit={(e) => {
            e.preventDefault();
            void props.spara(
              "vinter",
              async () => {
                const t = [av, pa, under, over].map(lasTal);
                if (!t.every(Number.isFinite)) throw new Felsvar("Skriv gränserna i procent.", 400);
                const [avP, paP, underP, overP] = t as number[];
                return api.andraPlats(id, {
                  vinter: { av: avP, pa: paP, varningUnder: underP, varningOver: overP },
                });
              },
              "Gränserna är sparade.",
            );
          }}
        >
          <h3>Gränserna</h3>
          {(
            [
              ["Laddaren av vid (%)", av, setAv],
              ["Laddaren på vid (%)", pa, setPa],
              ["Varning under (%)", under, setUnder],
              ["Varning över (%)", over, setOver],
            ] as const
          ).map(([etikett, varde, satt]) => (
            <label key={etikett}>
              {etikett}
              <input
                inputMode="decimal"
                autoComplete="off"
                value={varde}
                onInput={(e) => satt(e.currentTarget.value)}
              />
            </label>
          ))}
          <p class="liten">
            Laddaren på mellan {g.pa.min} och {g.pa.max} %, och laddaren av mellan {g.av.min} och{" "}
            {g.av.max} %, minst 5 procentenheter över på. Varningen under ska vara under på, och
            varningen över över av. Förval: av vid {g.av.forval} %, på vid {g.pa.forval} %, och
            varningen under {g.varningUnder.forval} och över {g.varningOver.forval} %.
          </p>
          <div class="knapprad">
            <button class="knapp-andra" type="submit">
              Spara gränserna
            </button>
            {[g.av, g.pa, g.varningUnder, g.varningOver].some((x) => x.installd) && (
              <button
                type="button"
                class="knapp-andra"
                onClick={() =>
                  props.spara(
                    "vinter",
                    () =>
                      api.andraPlats(id, {
                        vinter: { av: null, pa: null, varningUnder: null, varningOver: null },
                      }),
                    "Gränserna är återställda till förvalen.",
                  )
                }
              >
                Återställ till förvalen
              </button>
            )}
          </div>
        </form>
      )}
      {props.beskedrad}
    </Kort>
  );
}
